import { Modal, Input, Empty, Spin } from "antd";
import {
    CalendarOutlined,
    SearchOutlined,
    UserOutlined,
    MedicineBoxOutlined,
    BankOutlined,
    RightOutlined,
    DownOutlined,
} from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { t } from "i18next";
import moment from "moment";
import ApiService from "../../service/ApiService";
import Helper from "../../Helper";

const ACCENT = '#1890ff';
const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;

const CAT_COLORS = {
    patients: '#1890ff',
    appointments: '#13c2c2',
    operations: '#722ed1',
    institutions: '#fa8c16',
};

const tint = (hex, alphaHex = '15') => `${hex}${alphaHex}`;

function ResultRow({ icon, color, primary, secondary, extra, meta, onClick }) {
    const [pressed, setPressed] = useState(false);
    const [hovered, setHovered] = useState(false);
    // Tıklanabilir mi? onClick yoksa hover/press effect ve chevron kaldırılır.
    const interactive = typeof onClick === 'function';
    const bg = !interactive
        ? 'transparent'
        : (pressed ? tint(color, '33') : (hovered ? tint(color, '15') : 'transparent'));
    return (
        <div
            onClick={interactive ? onClick : undefined}
            onTouchStart={interactive ? () => setPressed(true) : undefined}
            onTouchEnd={interactive ? () => setPressed(false) : undefined}
            onTouchCancel={interactive ? () => setPressed(false) : undefined}
            onMouseDown={interactive ? () => setPressed(true) : undefined}
            onMouseUp={interactive ? () => setPressed(false) : undefined}
            onMouseEnter={interactive ? () => setHovered(true) : undefined}
            onMouseLeave={interactive ? () => { setHovered(false); setPressed(false); } : undefined}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 8px',
                borderBottom: '1px solid #f5f5f5',
                cursor: interactive ? 'pointer' : 'default',
                background: bg,
                transition: 'background 120ms ease',
                WebkitTapHighlightColor: 'transparent',
            }}
        >
            <div style={{
                width: 34, height: 34, borderRadius: '50%',
                background: tint(color, '22'), color: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, flexShrink: 0,
            }}>
                {icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {primary}
                </div>
                {secondary && (
                    <div style={{ fontSize: 12, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {secondary}
                    </div>
                )}
                {extra && (
                    <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {extra}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                {meta && <div style={{ fontSize: 11, color: '#bfbfbf' }}>{meta}</div>}
                {interactive && <RightOutlined style={{ fontSize: 10, color: '#d9d9d9' }} />}
            </div>
        </div>
    );
}

function CategorySection({ title, count, color, collapsed, onToggle, children }) {
    if (count === 0) return null;
    return (
        <div style={{ marginTop: 12 }}>
            <div
                onClick={onToggle}
                style={{
                    fontSize: 12,
                    color: color,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    padding: '8px 10px',
                    background: tint(color, '12'),
                    borderLeft: `3px solid ${color}`,
                    borderRadius: 4,
                    marginBottom: 4,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    userSelect: 'none',
                    WebkitTapHighlightColor: 'transparent',
                }}
            >
                <span>
                    {title} <span style={{ color: '#8c8c8c', fontWeight: 500 }}>({count})</span>
                </span>
                {collapsed
                    ? <RightOutlined style={{ fontSize: 11 }} />
                    : <DownOutlined style={{ fontSize: 11 }} />}
            </div>
            {!collapsed && children}
        </div>
    );
}

export default function SearchModal({ open, onClose }) {
    const history = useHistory();
    // Persistent — modal kapanınca state korunur, tekrar açılınca aynı görüntü.
    const [searchText, setSearchText] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState({});
    const abortRef = useRef(null);
    const debounceRef = useRef(null);
    const inputRef = useRef(null);

    // Modal açıldığında input'a focus + sanal klavye tetikle.
    // Provider'daki helper-input'a tap anında focus verildiği için sanal klavye
    // zaten açık; burada gerçek arama input'una focus transfer ediyoruz.
    // Birden fazla aşamada deneme: hemen, animasyon ortasında, animasyon sonu.
    useEffect(() => {
        if (!open) return;
        const tryFocus = () => {
            try {
                inputRef.current?.focus?.({ cursor: 'all' });
                inputRef.current?.input?.focus?.();
            } catch { /* ignore */ }
        };
        tryFocus();
        const t1 = setTimeout(tryFocus, 120);
        const t2 = setTimeout(tryFocus, 320);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [open]);

    // Debounce + abort
    useEffect(() => {
        if (!open) return;

        const text = searchText.trim();
        if (text.length < MIN_CHARS) {
            setResults(null);
            setLoading(false);
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            if (abortRef.current) {
                try { abortRef.current.aborted = true; } catch { /* ignore */ }
            }
            const myToken = { aborted: false };
            abortRef.current = myToken;

            setLoading(true);
            const res = await ApiService.Search.Live(text);
            if (myToken.aborted) return;

            setLoading(false);
            setResults(res?.success ? res.data : null);
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchText, open]);

    const handleClose = () => {
        if (abortRef.current) abortRef.current.aborted = true;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onClose && onClose();
    };

    const navigate = (url) => {
        handleClose();
        history.push(url);
    };

    const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

    const renderResults = () => {
        const text = searchText.trim();
        if (text.length === 0) {
            return (
                <div style={{ padding: '40px 16px', textAlign: 'center', color: '#bfbfbf' }}>
                    <SearchOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                    <div style={{ fontSize: 13 }}>{t('Type to search')}</div>
                </div>
            );
        }
        if (text.length < MIN_CHARS) {
            return (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: '#bfbfbf', fontSize: 12 }}>
                    {t('Type at least 2 characters')}
                </div>
            );
        }
        if (loading && !results) {
            return (
                <div style={{ padding: 32, textAlign: 'center' }}>
                    <Spin />
                </div>
            );
        }
        if (!results) return null;

        const total = (results.patients?.length || 0)
            + (results.appointments?.length || 0)
            + (results.operations?.length || 0)
            + (results.institutions?.length || 0);

        if (total === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('No results')}
                    style={{ padding: 24 }}
                />
            );
        }

        return (
            <>
                <CategorySection
                    title={t('Patients')}
                    count={results.patients?.length || 0}
                    color={CAT_COLORS.patients}
                    collapsed={collapsed.patients}
                    onToggle={() => toggle('patients')}
                >
                    {results.patients?.map(p => (
                        <ResultRow
                            key={`p${p.id}`}
                            icon={<UserOutlined />}
                            color={CAT_COLORS.patients}
                            primary={p.name?.trim() || t('(no name)')}
                            secondary={[p.customId, p.phone1, p.email].filter(Boolean).join(' • ')}
                            onClick={() => navigate(`/app/patients/${p.id}/appointments`)}
                        />
                    ))}
                </CategorySection>

                <CategorySection
                    title={t('Appointments')}
                    count={results.appointments?.length || 0}
                    color={CAT_COLORS.appointments}
                    collapsed={collapsed.appointments}
                    onToggle={() => toggle('appointments')}
                >
                    {results.appointments?.map(a => {
                        const currency = a.currency || 'TRY';
                        // null = "gizli" (VIEW_PRICE izni ve sorumlu değil); 0 = gerçek sıfır fiyat
                        const canSeePrice = a.totalPrice != null;
                        const balance = a.balance ?? 0;
                        const showBalance = canSeePrice && balance > 0;
                        return (
                            <ResultRow
                                key={`a${a.id}`}
                                icon={<CalendarOutlined />}
                                color={CAT_COLORS.appointments}
                                primary={a.patientName?.trim() || t('(no name)')}
                                secondary={[a.unitName, a.note].filter(Boolean).join(' • ')}
                                extra={a.operationNames?.length ? a.operationNames.join(' • ') : null}
                                meta={(
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, fontSize: 11 }}>
                                        <span style={{ color: '#bfbfbf' }}>
                                            {moment(a.startDate).format('DD.MM.YY HH:mm')}
                                        </span>
                                        {canSeePrice && (
                                            <span style={{ color: '#595959', fontWeight: 500 }}>
                                                {Helper.Currency.format(a.totalPrice, { currency })}
                                            </span>
                                        )}
                                        {showBalance && (
                                            <span style={{ color: '#fa8c16', fontWeight: 500 }}>
                                                {Helper.Currency.format(balance, { currency })} {t('balance')}
                                            </span>
                                        )}
                                    </div>
                                )}
                                onClick={() => navigate(`/app/patients/${a.patientId}/appointments/${a.id}`)}
                            />
                        );
                    })}
                </CategorySection>

                <CategorySection
                    title={t('Operations')}
                    count={results.operations?.length || 0}
                    color={CAT_COLORS.operations}
                    collapsed={collapsed.operations}
                    onToggle={() => toggle('operations')}
                >
                    {results.operations?.map(o => (
                        // Operation satırı tıklanabilir değil — default fiyat referansı için bilgi amaçlı
                        <ResultRow
                            key={`o${o.id}`}
                            icon={<MedicineBoxOutlined />}
                            color={CAT_COLORS.operations}
                            primary={o.name}
                            secondary={[o.modality, o.sutCode].filter(Boolean).join(' • ')}
                            meta={o.price != null ? (
                                <span style={{ fontSize: 12, color: '#595959', fontWeight: 500 }}>
                                    {Helper.Currency.format(o.price)}
                                </span>
                            ) : null}
                        />
                    ))}
                </CategorySection>

                <CategorySection
                    title={t('Institutions')}
                    count={results.institutions?.length || 0}
                    color={CAT_COLORS.institutions}
                    collapsed={collapsed.institutions}
                    onToggle={() => toggle('institutions')}
                >
                    {results.institutions?.map(i => (
                        <ResultRow
                            key={`i${i.id}`}
                            icon={<BankOutlined />}
                            color={CAT_COLORS.institutions}
                            primary={i.name}
                            secondary={[i.address, i.phoneNumber].filter(Boolean).join(' • ')}
                            // Kurum tıklaması → operations sayfası, ?institutionId ile pre-select edilir,
                            // üstte kurum kartı, dropdown otomatik K{id}'ye geçer, fiyatlar yüklenir
                            onClick={() => navigate(`/app/system/operations?institutionId=${i.id}`)}
                        />
                    ))}
                </CategorySection>
            </>
        );
    };

    return (
        <Modal
            title={(
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SearchOutlined style={{ color: ACCENT }} />
                    <span>{t('Search')}</span>
                    {loading && <Spin size="small" />}
                </span>
            )}
            open={open}
            onCancel={handleClose}
            footer={null}
            width={Math.min(560, window.innerWidth - 16)}
            // Mobilde centered KAPALI: iOS Safari klavye açıldığında window.innerHeight
            // ilk seferde güncellenmediği için ortalanmış modal klavyenin altına gömülüyor.
            // Üstte sabit konum → input her zaman görünür, klavye altı kaplasa bile sorun yok.
            centered={!isMobile}
            style={isMobile ? { top: 12, paddingBottom: 0, maxWidth: 'calc(100vw - 16px)' } : undefined}
            bodyStyle={{
                padding: 12,
                // Mobilde klavye alanını da hesaba kat; yüksekliği sınırla ki içerik scroll edilebilsin
                maxHeight: isMobile ? 'calc(100dvh - 120px)' : '70vh',
                overflowY: 'auto',
            }}
        >
            <Input
                ref={inputRef}
                placeholder={t('Search patients, appointments, operations, institutions...')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="large"
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                inputMode="search"
                enterKeyHint="search"
            />
            {renderResults()}
        </Modal>
    );
}
